'use client';
import { CaptchaRequestMessage } from '@eliadmin/core/src/components/providers/captcha-form-message';

import { useSystemLabels } from '@eliadmin/core/src/hooks/use-system-labels';
import { newsletterSubscribe } from '@eliadmin/core/src/api/newsletter/newsletter-api';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutation } from '@tanstack/react-query';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import React from 'react';

export function Newsletter() {
  const emailId = React.useId();
  const systemLabels = useSystemLabels();
  const { t } = useTranslations();

  // state
  const [email, setEmail] = React.useState('');
  const [checked, setChecked] = React.useState(true);
  const [isSubscribed, setIsSubscribed] = React.useState(false);

  const subscribeMutation = useMutation({
    mutationFn: newsletterSubscribe,
    onSuccess: () => setIsSubscribed(true),
  });

  const locale = useLocaleStore((s) => s.currentLocale);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!checked || isSubscribed || !email || subscribeMutation.isPending) return;
    subscribeMutation.mutate({
      locale,
      body: {
        email,
        gdpr: checked,
        return_url: new URL(ROUTES.NEWSLETTER_CONFIRM(), env.NEXT_PUBLIC_APP_URL).toString(),
      },
    });
  }

  const GDPRlink =
    locale === 'sk' ? '/spracovanie-osobnych-udajov' : '/verarbeitung-personenbezogener-daten';

  return (
    <form
      className={cn(
        'relative flex w-full flex-col items-start justify-start gap-10 bg-primary px-4 py-8 sm:gap-12 sm:px-6 sm:py-10 md:px-8 lg:-mt-[72px] lg:max-w-[384px] lg:-translate-y-[121px] lg:gap-16 lg:px-8',
        'ea-homepage-newsletter__form',
      )}
      onSubmit={handleSubmit}
    >
<CaptchaRequestMessage error={subscribeMutation.error} />
      <p className="relative z-10 text-sm text-white sm:text-base">Newsletter</p>
      <div className={cn('relative z-10 flex w-full flex-col items-start justify-start gap-8')}>
        <div className="flex w-full flex-col items-start justify-start gap-2">
          <h2 className="text-[28px] font-bold leading-[1.1] tracking-tighter text-white lg:text-[38px] lg:leading-[1.1]">
            {t('text357')}
            <br />
            {t('text3597')}
          </h2>
          <p className="w-full max-w-[282px] text-sm leading-normal text-white sm:text-base">
            {t('text3598')}
          </p>
        </div>
        <div className={cn('flex w-full max-w-[640px] flex-col items-start justify-start gap-4')}>
          <Input
            aria-label={systemLabels('email')}
            id={emailId}
            type="email"
            name="email"
            placeholder={`${t('text3599')}...`}
            onChange={(e) => setEmail(e.target.value)}
            className={{
              wrapper: cn('w-full bg-white', 'ea-homepage-newsletter__form-item-email-input'),
            }}
          />
          <Button
            type="submit"
            variant={'primary-foreground'}
            className={cn('w-full', 'ea-homepage-newsletter__form-item-email-submit-button')}
            disabled={email.length === 0 || !checked || isSubscribed}
            isLoading={subscribeMutation.isPending}
          >
            {t('text185')}
          </Button>
        </div>
        {isSubscribed && <span className={cn('font-semibold text-success')}>{t('text234')}</span>}
        <p className="w-full max-w-[282px] text-xs text-white sm:text-sm">
          {t('text3600')}{' '}
          <Link prefetch={false} href={GDPRlink} className="underline" target="_blank">
            {t('text614')}
          </Link>{' '}
          {t('text3389')}
        </p>
      </div>
      <Image
        src={'/images/smaller-overlay.png'}
        alt="Newsletter"
        width={0}
        height={0}
        className="absolute inset-0 z-0 h-full w-full object-cover opacity-10 mix-blend-screen"
        sizes="100vw"
      />
    </form>
  );
}
